
import Accordion from "../../components/application/Accordion/Accordion.vue";
import AccordionItem from "../../components/application/Accordion/AccordionItem.vue";
import {
  checkDailyDecoinLimit,
  dispatchErrorBox,
  dispatchSuccessBox,
  formatHour,
  formattedDate,
  formattedDuration,
  getLocalFormatDate,
  isCirculoTemplate,
  scrollSmoothToElement,
  scrollToTop
} from "../../components/mixins";
import { computed, ComputedRef, onBeforeMount, onMounted, ref, Ref } from "vue";
import {
  Course,
  CourseCertificate,
  Instructor,
  User
} from "../../../../domain/entities/entities";
import { useStore } from "vuex";
import { wrapLoading } from "../../utils/wrap_loading";
import { dispatchError } from "../../utils/error_handling";
import TUIViewer from "../../components/application/TUIViewer.vue";
import {
  Menu,
  TrainingCourse
} from "../../../../domain/entities/courseEvent/training_course";
import CircularImage from "./CircularImage.vue";
import CourseDetails from "./CourseDetails.vue";
import SectionDivisor from "../../components/application/SectionDivisor.vue";
import ActionButton from "../../components/application/buttons/ActionButton.vue";
import FakeIframe from "../../components/application/FakeIframe.vue";
import { useRoute } from "vue-router";
import CustomDropdown from "../../components/application/modals/CustomDropdown.vue";
import { addCertificateToLinkedIn } from "./linkedinUtils";

const __default__ = {
  methods: {
    isCirculoTemplate,
    formattedDate
  },
  components: {
    CustomDropdown,
    FakeIframe,
    ActionButton,
    SectionDivisor,
    CourseDetails,
    CircularImage,
    TUIViewer,
    AccordionItem,
    Accordion
  },
  props: {
    trainingCourseID: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const store = useStore();
    const route = useRoute();

    const selectedInstructorIndex: Ref<number> = ref(-1);

    const trainingCourse: ComputedRef<TrainingCourse> = computed(
      () => store.getters["training_course_module/trainingCourse"]
    );

    const instructors: ComputedRef<Instructor[]> = computed(
      () => store.getters["training_course_module/instructors"]
    );

    const enrolledUsers: ComputedRef<User[]> = computed(
      () => store.getters["training_course_module/enrolledUsers"]
    );

    const isUserEnrolled: ComputedRef<boolean> = computed(
      () => store.getters["training_course_module/isUserEnrolled"]
    );

    const isUserAttendance: ComputedRef<boolean> = computed(
      () => store.getters["training_course_module/isUserAttendance"]
    );

    const isUserPercentage: ComputedRef<number> = computed(() => {
      return store.getters["training_course_module/userPercentage"];
    });

    const currentMenu: ComputedRef<Menu> = computed(
      () => store.getters["training_course_module/currentMenu"]
    );

    const certificateTraining: ComputedRef<string> = computed(
      () => store.getters["userProfile_module/certificateTraining"]
    );

    const showCertificateButton = computed(() => {
      return (
        trainingCourse.value.haveCertification && isUserPercentage.value >= 75
      );
    });

    const backgroundImage: Ref<string> = ref("");

    const numberOfEnrollUsers: Ref<number> = ref(0);

    const attendanceToken = route.query.token;

    const menuIDQrCode = route.query.id;

    const currentDate = new Date().toISOString().split("T")[0];
    const currentTime = new Intl.DateTimeFormat("pt-BR", {
      hour: "2-digit",
      minute: "2-digit"
    }).format(new Date());

    const isOutsidePeriodAttendanceAllowed =
      route.query.isOutsidePeriodAttendanceAllowed;

    const showConfirmPresenceButton = computed(() => {
      return (
        attendanceToken && !isUserAttendance.value && canMarkAttendance.value
      );
    });

    const isTrainingCompleted = computed(() => {
      if (!currentMenu.value) return false;

      if (
        currentMenu.value.startDate === currentDate &&
        currentTime >= currentMenu.value.endHour
      )
        return true;

      return currentMenu.value.startDate < currentDate;
    });

    const isTrainingNotCompleted = computed(() => {
      if (!currentMenu.value) return false;

      if (
        currentMenu.value.startDate === currentDate &&
        currentTime <= currentMenu.value.startHour
      )
        return true;

      return currentMenu.value.startDate > currentDate;
    });

    const isTrainingInProgress = computed(() => {
      if (!currentMenu.value) return false;

      return (
        currentMenu.value.startDate === currentDate &&
        currentTime >= currentMenu.value.startHour &&
        currentTime <= currentMenu.value.endHour
      );
    });

    const canMarkAttendance = computed(() => {
      if (isTrainingNotCompleted.value) return true;
      if (isTrainingInProgress.value) return true;
      return (
        isTrainingCompleted.value && isOutsidePeriodAttendanceAllowed === "true"
      );
    });

    onBeforeMount(async () => {
      await wrapLoading(async () => {
        await loadTrainingCourse();
        numberOfEnrollUsers.value = enrolledUsers.value
          ? enrolledUsers.value.length
          : 0;
        backgroundImage.value = `url(${trainingCourse.value.bannerURL})`;
        await loadInstructors();
        scrollToTop();
        showSeeMoreButton();

        if (!canMarkAttendance.value && attendanceToken) {
          await dispatchErrorBox("Não é possível marcar presença!");
        }
      });
    });

    onMounted(async () => {
      if (menuIDQrCode) {
        await setMenuIDQrCode(parseInt(menuIDQrCode.toString()));
      }
    });

    const dropdownCertificateItems = computed(() => {
      return [
        {
          label: "Baixar certificado",
          description: "Baixe o certificado em seu dispositivo",
          action: () => openCertificate(trainingCourse.value.id),
          disabled: false,
          icon: require("../../assets/static/icons/certificate-blue.svg")
        },
        {
          label: "Adicionar ao LinkedIn",
          description:
            "Redirecione-se ao LinkedIn para adicionar o certificado",
          action: async () => await addToLinkedIn(trainingCourse.value),
          disabled: false,
          icon: require("../../assets/static/icons/linkedin.svg")
        }
      ];
    });

    const addToLinkedIn = async (
      trainingCourse: TrainingCourse
    ): Promise<void> => {
      try {
        await wrapLoading(async () => {
          await store.dispatch(
            "userProfile_module/getCertificate",
            trainingCourse.id
          );

          const certificateUrl = (certificateTraining.value || "").replace(
            /\\/g,
            "/"
          );
          if (!certificateUrl) return;

          const lastMenu =
            trainingCourse.menus?.[trainingCourse.menus.length - 1];
          const issueDate = lastMenu
            ? new Date(lastMenu.startDate)
            : new Date();

          await addCertificateToLinkedIn({
            name: trainingCourse.title,
            certificateUrl,
            certificateId: String(trainingCourse.id),
            issueYear: issueDate.getFullYear(),
            issueMonth: issueDate.getMonth() + 1
          });
        });
      } catch (e) {
        await dispatchError(e);
      }
    };

    const setMenuIDQrCode = async (id: number): Promise<void> => {
      try {
        await store.dispatch("training_course_module/setMenuIDQrCode", id);
      } catch (e) {
        await dispatchError(e);
      }
    };

    const enrollUser = async (): Promise<void> => {
      try {
        await store.dispatch(
          "training_course_module/enroll",
          props.trainingCourseID
        );
        await dispatchSuccessBox("Inscrição realizada com sucesso!");
        numberOfEnrollUsers.value += 1;
      } catch (e) {
        console.error(e);
        throw e;
      }
    };

    const unEnrollUser = async (): Promise<void> => {
      try {
        await store.dispatch(
          "training_course_module/unenroll",
          props.trainingCourseID
        );
        await dispatchSuccessBox("Inscrição cancelada com sucesso!");
        numberOfEnrollUsers.value -= 1;
      } catch (e) {
        console.error(e);
        throw e;
      }
    };

    const formatMenus = (
      startDate: string,
      duration: number,
      startHour: string,
      endHour: string,
      title: string
    ): string => {
      if (!startHour || !endHour)
        return `${getLocalFormatDate(startDate)}
        - ${formattedDuration(duration)}: ${title}`;

      return `${getLocalFormatDate(startDate)} - ${formatHour(
        startHour
      )} às ${formatHour(endHour)}: ${title}`;
    };

    /** Scroll to the selected instructor. */
    const goToInstructor = (index: number): void => {
      const data = index
        ? `[data-instructor-index='${index - 1}']`
        : `[data-instructors]`;
      scrollSmoothToElement(data);
      setSelectedInstructor(index);
    };

    const setSelectedInstructor = (index: number): void => {
      selectedInstructorIndex.value = index;
    };

    const loadTrainingCourse = async (): Promise<void> => {
      await store.dispatch(
        "training_course_module/get",
        parseInt(props.trainingCourseID)
      );
    };

    const loadInstructors = async () => {
      try {
        await wrapLoading(async () => {
          await store.dispatch(
            "training_course_module/getInstructors",
            props.trainingCourseID
          );
        });
      } catch (e) {
        await dispatchError(e);
      }
    };

    /** Returns a string with a period in the following format: "30/03/2023 até 01/04/2023".
     * @param startDate start of the period in the format: "2023-03-30 13:50:00".
     * @param endDate end of the period in the format: "2023-03-30 13:50:00".
     */
    const formatDate = (startDate: string, endDate: string): string => {
      return !endDate || startDate === endDate
        ? formattedDate(startDate)
        : `${formattedDate(startDate)} até ${formattedDate(endDate)}`;
    };

    function seeMoreDescription(index: number): void {
      const buttonReadMore = document.querySelector(
        `[data-see-more="${index}"]`
      ) as HTMLButtonElement;
      const biography = document.querySelector(
        `[data-biography="${index}"]`
      ) as HTMLParagraphElement;

      if (buttonReadMore && biography) {
        if (buttonReadMore.innerText === "ver mais") {
          buttonReadMore.innerText = "ver menos";
          biography.classList.remove("dots");
        } else {
          buttonReadMore.innerText = "ver mais";
          biography.classList.add("dots");
        }
      }
    }

    function showSeeMoreButton(): void {
      if (!instructors.value) return;
      instructors.value.forEach((instructor: Instructor, index: number) => {
        const buttonReadMore = document.querySelector(
          `[data-see-more="${index}"]`
        ) as HTMLButtonElement;
        const biography = document.querySelector(
          `[data-biography="${index}"]`
        ) as HTMLParagraphElement;

        if (biography.offsetHeight > 48) {
          buttonReadMore.style.display = "flex";
          biography.classList.add("dots");
        } else buttonReadMore.style.display = "none";
      });
    }

    const onClickEnroll = async (): Promise<void> => {
      try {
        await wrapLoading(async () => {
          if (isUserEnrolled.value) {
            await unEnrollUser();
          } else {
            await enrollUser();
          }
        });
      } catch (e) {
        await dispatchError(e);
      }
    };

    const isEnrollmentOpen = computed(() => {
      const today = new Date().toISOString().slice(0, 10);
      if (
        today >= trainingCourse.value.enrollmentStart &&
        today <= trainingCourse.value.enrollmentEnd
      )
        return true;
      return false;
    });
    const showEnrollButton = computed(() => {
      return (
        isEnrollmentOpen.value && !isUserEnrolled.value && !attendanceToken
      );
    });
    const showUnEnrollButton = computed(() => {
      return (
        isEnrollmentOpen.value &&
        !isUserAttendance.value &&
        isUserEnrolled.value &&
        !attendanceToken
      );
    });
    const showIsentAttendance = computed(() => {
      return !isUserAttendance.value && isUserEnrolled.value;
    });

    const showIsAttendance = computed(() => {
      return isUserAttendance.value && isUserEnrolled.value;
    });

    async function confirmAttendance() {
      try {
        await wrapLoading(async () => {
          if (!attendanceToken && !menuIDQrCode) return;

          await store.dispatch("training_course_module/confirmCheckinQrCode", {
            token: attendanceToken,
            id: menuIDQrCode
          });
          await dispatchSuccessBox("Presença confirmada!");
          await checkDailyDecoinLimit();
        });
      } catch (e) {
        await dispatchError(e);
      }
    }

    const openCertificate = async (trainingID: number): Promise<void> => {
      try {
        if (trainingCourse.value.certificatePath) {
          window.open(trainingCourse.value.certificatePath, "_blank");
        } else {
          await generateCertificate(trainingID);
        }
      } catch (e) {
        await dispatchError(e);
      }
    };

    const generateCertificate = async (trainingID: number): Promise<void> => {
      await wrapLoading(async () => {
        await store.dispatch("userProfile_module/getCertificate", trainingID);
      }, "Gerando certificado...");

      window.open(certificateTraining.value, "_blank");
    };

    return {
      trainingCourse,
      selectedInstructorIndex,
      isUserEnrolled,
      instructors,
      backgroundImage,
      enrolledUsers,
      numberOfEnrollUsers,
      showEnrollButton,
      showUnEnrollButton,
      showIsentAttendance,
      showIsAttendance,
      showConfirmPresenceButton,
      showCertificateButton,
      dropdownCertificateItems,
      openCertificate,
      confirmAttendance,
      enrollUser,
      unEnrollUser,
      goToInstructor,
      formatDate,
      seeMoreDescription,
      formattedDuration,
      formatMenus,
      onClickEnroll
    };
  }
};

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "9d402d1a": (_ctx.backgroundImage)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__